import { SyntheticEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import useWindowResize from '@picsart/rc/useWindowResize';
import View from '@picsart/view/View';
import Image from '@picsart/image/Image';

import getGridImageSizes from 'utils/images/getGridImageSizes';
import CropWrapper from 'pages/Batch/components/CropItem/CropWrapper';
import useMemoSelector from 'hooks/useMemoSelector';
import { getCropRotate, getCropFlip } from 'store/batch/selectors';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';

import useStyles from './styles';

interface ICropItem {
  imgSrc: string;
  boxWidth: number;
  boxHeight: number;
  itemKey: string;
}

const CropItem = ({ imgSrc, boxWidth, boxHeight, itemKey }: ICropItem) => {
  const classes = useStyles();

  const initialImageRef = useRef<HTMLImageElement>(null);
  const newImageRef = useRef<HTMLImageElement>(null);
  const initialImageSrc = useRef(imgSrc);
  const { areImagesResizing } = useBatchData();
  const arePrevImagesResizingRef = useRef(areImagesResizing);

  const { cropRotate, cropFlip } = useMemoSelector(state => ({
    cropRotate: getCropRotate(state),
    cropFlip: getCropFlip(state),
  }));

  const [{ width: currentWidth, height: currentHeight }, setImageBoxSize] = useState({
    width: '0',
    height: '0',
  });
  const [imgSizes, setImgSizes] = useState<number[]>([]);
  const [isNewImageReady, setIsNewImageReady] = useState(false);

  useEffect(() => {
    if (arePrevImagesResizingRef.current && !areImagesResizing) {
      initialImageSrc.current = imgSrc;
    }
    arePrevImagesResizingRef.current = areImagesResizing;
  });

  const handleImageSize = useCallback(() => {
    const image = newImageRef.current || initialImageRef.current;

    if (image) {
      const imgBoxSizes = getGridImageSizes(boxWidth, boxHeight, image);
      setImageBoxSize(imgBoxSizes);
    }
  }, [boxHeight, boxWidth]);

  useEffect(() => {
    initialImageRef.current && setImgSizes([initialImageRef.current?.width, initialImageRef.current?.height]);
  }, [currentWidth, currentHeight, cropRotate, cropFlip.x, cropFlip.y]);

  useWindowResize(() => {
    handleImageSize();
    setImgSizes([initialImageRef.current?.width || 0, initialImageRef.current?.height || 0]);
  });

  const onLoadInitialImage = (event: SyntheticEvent<HTMLImageElement>) => {
    initialImageSrc.current = event.currentTarget.src;
    handleImageSize();
  };

  const onLoadNewImage = () => {
    handleImageSize();
    setIsNewImageReady(true);
  };

  const isDrawCropWrapper = Boolean(imgSizes.length && imgSizes[0] !== 0 && imgSizes[1] !== 0);

  const newImageSrc = !arePrevImagesResizingRef.current && initialImageSrc.current !== imgSrc ? imgSrc : null;

  return (
    <>
      {!isNewImageReady && (
        <View
          className={classNames(classes.gridImageWrap, classes.cropActive)}
          style={{ width: currentWidth, height: currentHeight }}
        >
          <Image
            src={initialImageSrc.current}
            ref={initialImageRef}
            className={classes.gridImage}
            onLoad={onLoadInitialImage}
            style={{ transform: `rotate(${cropRotate || 0}deg) scale(${(cropFlip?.x || 1)}, ${(cropFlip?.y || 1)})` }}
          />
          {isDrawCropWrapper && <CropWrapper imgSizes={imgSizes} itemKey={itemKey} />}
        </View>
      )}
      {newImageSrc && (
        <View
          className={classNames(classes.gridImageWrap, classes.cropActive)}
          style={{ width: currentWidth, height: currentHeight, display: !isNewImageReady ? 'none' : 'block' }}
        >
          <Image
            src={newImageSrc}
            ref={newImageRef}
            onLoad={onLoadNewImage}
            className={classes.gridImage}
          />
        </View>
      )}
    </>
  );
};

export default memo(CropItem);
